/**
 * localStorage utilities for license credentials.
 *
 * Two distinct values are persisted today:
 *
 * - **License key** (JWT): a signed token carrying the user's entitlement.
 *   Validated against the server on app boot.
 * - **Redemption code**: the human-readable bearer token (e.g.,
 *   `PIPHAUTHOR_W15V_EJ4T_1CFF`) returned to the original redeemer. The
 *   *persistent* identity — used to re-mint a fresh JWT on each app boot
 *   via the idempotent /redeemCode flow (PIPH-290 / PIPH-291).
 *
 * Both are stored side by side. The code is the long-lived identity;
 * the JWT is the ephemeral session token. Future tightening (PIPH-291
 * follow-up) may move the JWT to in-memory-only.
 */

const LICENSE_KEY_STORAGE_KEY = 'alchemie_license_key';
const REDEMPTION_CODE_STORAGE_KEY = 'alchemie_redemption_code';

/**
 * Save a license key to localStorage
 */
export function saveKey(key: string): void {
  try {
    localStorage.setItem(LICENSE_KEY_STORAGE_KEY, key);
    console.log('[Auth] License key saved to localStorage');
  } catch (error) {
    console.error('[Auth] Failed to save license key to localStorage:', error);
    throw new Error('Failed to save license key');
  }
}

/**
 * Retrieve the stored license key from localStorage
 */
export function getKey(): string | null {
  try {
    const key = localStorage.getItem(LICENSE_KEY_STORAGE_KEY);
    if (key) {
      console.log('[Auth] License key retrieved from localStorage');
    }
    return key;
  } catch (error) {
    console.error('[Auth] Failed to retrieve license key from localStorage:', error);
    return null;
  }
}

/**
 * Remove the license key from localStorage
 */
export function clearKey(): void {
  try {
    localStorage.removeItem(LICENSE_KEY_STORAGE_KEY);
    console.log('[Auth] License key cleared from localStorage');
  } catch (error) {
    console.error('[Auth] Failed to clear license key from localStorage:', error);
  }
}

/**
 * Save a redemption code to localStorage. This is the persistent
 * identity that survives sign-outs in some flows — sign-out clears it
 * via `clearLicenseSession`, but tab close / page reload preserves it.
 */
export function saveCode(code: string): void {
  try {
    localStorage.setItem(REDEMPTION_CODE_STORAGE_KEY, code);
    console.log('[Auth] Redemption code saved to localStorage');
  } catch (error) {
    console.error('[Auth] Failed to save redemption code to localStorage:', error);
    throw new Error('Failed to save redemption code');
  }
}

/**
 * Retrieve the stored redemption code from localStorage. Returns null
 * if no code has been persisted (pre-rework users, or fresh installs).
 */
export function loadCode(): string | null {
  try {
    return localStorage.getItem(REDEMPTION_CODE_STORAGE_KEY);
  } catch (error) {
    console.error('[Auth] Failed to retrieve redemption code from localStorage:', error);
    return null;
  }
}

/**
 * Remove the stored redemption code. Called on sign-out and on
 * permanent code-rejection (revoked / expired / wrong-user errors).
 * NOT called on transient failures like network errors.
 */
export function clearCode(): void {
  try {
    localStorage.removeItem(REDEMPTION_CODE_STORAGE_KEY);
    console.log('[Auth] Redemption code cleared from localStorage');
  } catch (error) {
    console.error('[Auth] Failed to clear redemption code from localStorage:', error);
  }
}
